<!-- Source dialog (#sourceEditModal, data in data.sources): new or existing source. Fields in order: thumbnail (click,
     drop or image URL; ImageBox.vue), project (with at least two projects; move, or copy to another project, for a saved
     source), title (required), category (KindPicker.vue: fixed ones and the user's own), tags of the chosen project for
     the common themes (several; "+" creates one there), link of the site (may stay empty; "https://" is added on save),
     fact-checking notes with @mentions. While the category is empty and was not chosen by hand, typing the link
     proposes one (guessSourceKind: papers, video, TV, podcasts, socials, books, data, official documents). A new
     source starts with the tag of the Sources page's tag row (TAGSTATE.src.filter), its open category and the project
     of new items; a link or text dropped on the Sources page fills
     link and title (prefill, src/stores/drop.ts) and the title is selected. Delete asks first.
     Opened with openSourceModal(id?, prefill?) (create button, Sources page, search, mentions, drop) through the
     dialogs store; logic in src/features/sources.ts. Like the legacy dialog, it closes only with ‹, × or the buttons; Enter in the title, Ctrl/Cmd+S and Ctrl/Cmd+Enter save.
     From 1024 px: the thumbnail on the left of project, title, category and tags. -->
<script setup lang="ts">
import EditDialogFooter from '../fields/EditDialogFooter.vue'
import { INPUT_CLS, requiredCls } from '../../utils/fieldCls'
import { nextTick, onMounted, reactive, ref, watch } from 'vue'
import KindPicker from '../fields/KindPicker.vue'
import RefUsage from '../video/RefUsage.vue'
import AppDialog from '../AppDialog.vue'
import ImageBox from '../fields/ImageBox.vue'
import ProjectPicker from '../fields/ProjectPicker.vue'
import TagPicker from '../fields/TagPicker.vue'
import MentionField from '../fields/MentionField.vue'
import { useI18n } from '../../composables/useI18n'
import { useDataStore } from '../../stores/data'
import { useSourcesStore } from '../../stores/sources'
import { deleteSource, guessSourceKind, saveSource, sourceForm, type SourcePrefill } from '../../features/sources'
import { validProject } from '../../features/projects'
import { newItemProject } from '../../stores/projects'
import { confirmDelete, uiAlertThen } from '../../stores/notices'
import { enterKey } from '../../features/dialogs'
import { finePointer } from '../../utils/media'
import { saveAppData } from '../../features/storage/save'

const props = defineProps<{ id: string | null; prefill?: SourcePrefill }>()
const emit = defineEmits<{ close: [] }>()
const { t } = useI18n()
const store = useDataStore()

const src = useSourcesStore()
const form = reactive(sourceForm(store.getData(), props.id, src.newSourceTag(), newItemProject(), src.newSourceKind()))
if (props.prefill) {
  if (props.prefill.title !== undefined) form.title = props.prefill.title
  if (props.prefill.url !== undefined) form.url = props.prefill.url
}

// The category guessed from the link, until the user chooses one by hand
const guessed = ref(!form.kind)
const guess = () => { if (guessed.value) form.kind = guessSourceKind(form.url) }
guess()
watch(() => form.url, guess)
function pickKind(k: string) { form.kind = k; guessed.value = false }

const titleInput = ref<HTMLInputElement | null>(null)
const tagPicker = ref<InstanceType<typeof TagPicker> | null>(null)
// Dropped link: the title is ready to be retyped; a new source starts in the title (with a mouse: no phone keyboard over the dialog)
onMounted(() => {
  if (props.prefill) nextTick(() => { titleInput.value?.focus(); titleInput.value?.select() })
  else if (!props.id && finePointer()) nextTick(() => titleInput.value?.focus())
})
// The title is required: shown in the main colour, with the cursor in it, after the message
const missing = ref(false)
watch(() => form.title, v => { if (v.trim()) missing.value = false })
const onEnter = enterKey(save)

function save() {
  if (!form.title.trim()) { missing.value = true; void uiAlertThen(t('Enter at least a title for the source!')).then(() => titleInput.value?.focus()); return }
  const tags = tagPicker.value ? tagPicker.value.flush() : form.tags
  const d = store.getData()
  const r = saveSource(d, props.id, { ...form, tags, projectId: validProject(d, form.projectId, newItemProject()) })
  if (!r) return
  saveAppData(d)
  emit('close')
}

async function remove() {
  if (!props.id) return
  if (!(await confirmDelete(t('Are you sure you want to delete this source?')))) return
  const d = store.getData()
  deleteSource(d, props.id)
  saveAppData(d)
  emit('close')
}
</script>

<template>
  <AppDialog id="sourceEditModal" name="source" title-id="sourceModalTitle" :title="id ? t('Edit source') : t('New source')" editing :save="save" @close="emit('close')">
    <div class="space-y-5 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-6 lg:items-start">
    <div>
      <ImageBox v-model="form.thumbnail" id="srcThumbBox" file-id="srcThumbFile" :placeholder="t('Change thumbnail')" url-field url-id="inputSourceThumbUrl" />
    </div>
    <div class="space-y-5">
    <ProjectPicker v-model="form.projectId" kind="source" :item-id="id" />
    <div>
      <label class="block text-xs font-bold text-gray-400 mb-2 tracking-wider">{{ t('TITLE') }}</label>
      <input id="inputSourceTitle" ref="titleInput" v-model="form.title" type="text" :placeholder="t('E.g. YouTube Trends report')"
        :class="requiredCls(missing)" @keydown.enter="onEnter">
    </div>

    <div>
      <label class="block text-xs font-bold text-gray-400 mb-2 tracking-wider">{{ t('CATEGORY') }} <span class="font-normal text-gray-500 tracking-normal">{{ t('(what kind of source it is)') }}</span></label>
      <KindPicker id="srcKindPicker" :model-value="form.kind" group="src" @update:model-value="pickKind" />
    </div>

    <div>
      <label class="block text-xs font-bold text-gray-400 mb-2 tracking-wider"><span>{{ t('TAGS') }}</span> <span class="font-normal text-gray-500 tracking-normal">{{ t('(the themes, one or more)') }}</span></label>
      <TagPicker id="srcTagPicker" ref="tagPicker" v-model="form.tags" kind="src" :project-id="form.projectId" />
    </div>
    </div>
    </div>

    <div>
      <label class="block text-xs font-bold text-gray-400 mb-2 tracking-wider">{{ t('SOURCE SITE URL') }}</label>
      <input id="inputSourceUrl" v-model="form.url" type="url" inputmode="url" autocapitalize="off" autocorrect="off" spellcheck="false" autocomplete="off" placeholder="https://..."
        :class="INPUT_CLS">
    </div>

    <div>
      <label class="block text-xs font-bold text-gray-400 mb-2 tracking-wider">{{ t('FACT-CHECKING NOTES') }}</label>
      <MentionField id="inputSourceNote" v-model="form.note" :placeholder="t('Reliability, verified data, remarks... (@ to mention)')"
        class="min-h-[5rem] w-full bg-darkinput border border-gray-800 rounded-lg p-4 text-gray-100 focus:outline-none focus:border-brand transition" />
    </div>

    <RefUsage group="src" :id="id" @open="emit('close')" />

    <template #footer>
      <EditDialogFooter delete-id="deleteSourceBtn" :can-delete="!!id" :delete-label="t('Delete source')" :save-label="t('Save source')" @delete="remove" @save="save" />
    </template>
  </AppDialog>
</template>
